import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Copy, Check, Share2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setData(data);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (!data) return;
    const first = (user?.name || "").split(" ")[0] || "I";
    const text = `${first} is using AddVendTour to find local events. Sign up with my link and your first month of Maker Pro is on me: ${data.share_url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Join me on AddVendTour", text, url: data.share_url });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Message copied — paste it anywhere");
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="referrals-page">
      <div className="overline" style={{ color: "var(--gold)" }}>Refer a maker</div>
      <h1 className="font-serif text-5xl mt-3">Grow the market together.</h1>
      <p className="mt-3 max-w-2xl text-sm" style={{ color: "var(--ink-soft)" }}>
        Every friend who joins AddVendTour Pro with your link earns you <strong>$19</strong> —
        one free month of Maker Pro, applied automatically to your next Stripe invoice.
        Your friend gets their first month free too. Everybody wins.
      </p>

      {err && (
        <div
          data-testid="referrals-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(212,88,58,0.10)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {!data && !err && (
        <div className="mt-10 text-sm" style={{ color: "var(--ink-soft)" }}>
          Loading your link…
        </div>
      )}

      {data && (
        <>
          <div
            data-testid="referrals-share-card"
            className="mt-10 rounded-lg border p-6 sm:p-8"
            style={{
              background: "linear-gradient(135deg, rgba(201,162,75,0.12), #fff)",
              borderColor: "rgba(0,0,0,0.06)",
              boxShadow: "0 20px 60px -30px rgba(21,65,35,0.30)",
            }}
          >
            <div className="overline flex items-center gap-2" style={{ color: "var(--gold)" }}>
              <Gift className="h-3.5 w-3.5" /> Your referral link
            </div>
            <div className="mt-2 font-mono text-lg break-all" data-testid="referrals-share-url">
              {data.share_url}
            </div>
            <div className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              Or share the code directly:
              <code
                data-testid="referrals-code"
                className="ml-2 px-2 py-0.5 rounded-sm font-mono"
                style={{ backgroundColor: "rgba(31,93,52,0.10)", color: "var(--forest)" }}
              >
                {data.code}
              </code>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={copy}
                data-testid="referrals-copy-btn"
                className="btn-primary rounded-full inline-flex items-center gap-2"
              >
                {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy link</>}
              </Button>
              <Button
                type="button"
                onClick={nativeShare}
                variant="outline"
                data-testid="referrals-share-btn"
                className="rounded-full inline-flex items-center gap-2"
              >
                <Share2 className="h-4 w-4" /> Share…
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat testId="stat-invited" label="Invited" value={data.stats.invited} />
            <Stat testId="stat-converted" label="Joined Pro" value={data.stats.converted} accent="var(--moss)" />
            <Stat testId="stat-pending" label="Still deciding" value={data.stats.pending} accent="var(--ink-soft)" />
            <Stat testId="stat-months" label="Free months earned" value={data.stats.months_earned} accent="var(--gold)" />
          </div>

          {data.pending_credit_cents > 0 && (
            <div
              data-testid="referrals-pending-credit"
              className="mt-6 rounded-md p-4 text-sm border"
              style={{
                backgroundColor: "rgba(31,93,52,0.06)",
                borderColor: "rgba(31,93,52,0.25)",
                color: "var(--forest)",
              }}
            >
              💰 You have <strong>${(data.pending_credit_cents / 100).toFixed(2)}</strong> in referral
              credit waiting. It will auto-apply the next time you subscribe to Maker Pro.
            </div>
          )}

          {/* Recent invitees */}
          <div className="mt-10">
            <div className="overline" style={{ color: "var(--terracotta)" }}>Recent invites</div>
            <div className="font-serif text-2xl mt-1">Who you&apos;ve brought in.</div>
            {data.recent.length === 0 ? (
              <div
                data-testid="referrals-empty"
                className="mt-6 rounded-lg border-2 border-dashed p-8 text-center text-sm"
                style={{ borderColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}
              >
                No invites yet. Share your link above to start earning free months.
              </div>
            ) : (
              <ul
                data-testid="referrals-recent-list"
                className="mt-4 divide-y"
                style={{ borderColor: "rgba(0,0,0,0.06)" }}
              >
                {data.recent.map((r, i) => (
                  <li
                    key={`${r.name}-${r.invited_at}-${i}`}
                    data-testid={`referrals-recent-${i}`}
                    className="py-4 flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="text-sm font-medium">{r.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
                        Signed up as {r.role} · {relTime(r.invited_at)}
                      </div>
                    </div>
                    {r.status === "rewarded" ? (
                      <span
                        className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: "rgba(111,125,85,0.15)", color: "var(--moss)" }}
                      >
                        <CheckCircle2 className="h-3 w-3" /> +$19 earned
                      </span>
                    ) : (
                      <span
                        className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: "rgba(0,0,0,0.05)", color: "var(--ink-soft)" }}
                      >
                        <Clock className="h-3 w-3" /> Waiting to upgrade
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            className="mt-14 text-xs"
            style={{ color: "var(--ink-soft)" }}
          >
            Fine print: 1 free month = $19 Stripe credit per successful referral (someone who upgrades to Maker Pro on any plan).
            Credits stack — the more people you refer, the more months you earn. Codes can&apos;t be combined with other promo codes at signup.
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testId, accent = "var(--terracotta)" }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border p-4"
      style={{ backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
        {label}
      </div>
      <div className="font-serif text-3xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
